import { useEffect, useState } from "react";
import axiosInstant from "../../api";
import { proposalExamplesEndpoint } from "../../api/endpoints";
import { ProposalExamplesResponse } from "./types";

// Demo videos + top Expert IQ experts for the selected product category
// (L2 selections expand to descendants server-side). Non-fatal: the
// strategy view and PDF simply omit the sections when empty.
export const useProposalExamples = (
    categoryId: number | null,
    contentTypeIds: number[],
    enabled: boolean,
    channelIds: number[] = [],
) => {
    const [examples, setExamples] = useState<ProposalExamplesResponse | null>(null);

    const signature = JSON.stringify({ categoryId, contentTypeIds, channelIds, enabled });

    useEffect(() => {
        if (!enabled || !categoryId) {
            setExamples(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axiosInstant.get<ProposalExamplesResponse>(
                    proposalExamplesEndpoint(categoryId, contentTypeIds, channelIds),
                );
                if (!cancelled) setExamples(data);
            } catch {
                if (!cancelled) setExamples(null);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    return examples;
};

// Same hook, plus a refetch handle for surfaces that edit demo defaults.
export const useProposalExamplesWithRefetch = (
    categoryId: number | null,
    contentTypeIds: number[],
    enabled: boolean,
    channelIds: number[] = [],
) => {
    const [examples, setExamples] = useState<ProposalExamplesResponse | null>(null);
    const [nonce, setNonce] = useState(0);
    const signature = JSON.stringify({ categoryId, contentTypeIds, channelIds, enabled, nonce });

    useEffect(() => {
        if (!enabled || !categoryId) {
            setExamples(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axiosInstant.get<ProposalExamplesResponse>(
                    proposalExamplesEndpoint(categoryId, contentTypeIds, channelIds),
                );
                if (!cancelled) setExamples(data);
            } catch {
                if (!cancelled) setExamples(null);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    return { examples, refetch: () => setNonce((n) => n + 1) };
};
